import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  IconButton,
  Chip,
  Typography,
  Button,
  Tooltip,
  CircularProgress,
  Alert,
  Stack,
  Badge,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Link,
  Menu,
  MenuItem,
  Checkbox,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Description as DescriptionIcon,
  LocalShipping as LocalShippingIcon,
} from '@mui/icons-material';
import { useAuth0 } from '@auth0/auth0-react';
import DataViewerSideBySide from './DataViewerSideBySide';

interface VerificationItem {
  report_id: string;
  filename: string;
  uploaded_at: string;
  confidence: number;
  status: string;
  uploaded_by?: string;
  page_number?: number;
  total_pages?: number;
  product_type?: string;
  meter_id?: string;
  verified?: boolean;
  verification_date?: string;
  verified_by?: string;
  missing_required_fields?: string[];
  fields_below_threshold?: string[];
  requires_human_review?: boolean;
  // Document-type differentiation (proving reports vs SGS crude tickets)
  document_type?: 'proving_report' | 'crude_ticket';
  vessel?: string;
  discharge_date?: string;
  has_bounding_boxes?: boolean;
}

const VerificationQueue: React.FC = () => {
  const { user, getIdTokenClaims } = useAuth0();
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [items, setItems] = useState<VerificationItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<VerificationItem[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedReport, setSelectedReport] = useState<any>(null);
  const [showDataViewer, setShowDataViewer] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMode, setFilterMode] = useState<'all' | 'needs-review' | 'low-confidence' | 'verified' | 'non-verified'>('needs-review');
  const [docTypeFilter, setDocTypeFilter] = useState<'all' | 'proving_report' | 'crude_ticket'>('all');
  const [refreshing, setRefreshing] = useState(false);
  const [userPreferences, setUserPreferences] = useState<any>(null);
  const [orderBy, setOrderBy] = useState<keyof VerificationItem>('uploaded_at');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [activeFilters, setActiveFilters] = useState<{
    product?: string[];
    page?: number[];
    meterId?: string[];
    date?: string[];
  }>({});
  const [anchorEl, setAnchorEl] = useState<{ [key: string]: HTMLElement | null }>({});
  const [filterOptions, setFilterOptions] = useState<{ [key: string]: any[] }>({});

  const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || 'https://fg0jf2txt9.execute-api.us-east-2.amazonaws.com/prod';

  // Fetch user preferences
  const fetchUserPreferences = useCallback(async () => {
    try {
      const tokenClaims = await getIdTokenClaims();
      const idToken = tokenClaims?.__raw;

      const response = await fetch(
        `${API_ENDPOINT}/preferences?email=${encodeURIComponent(user?.email || '')}`,
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        }
      );

      if (response.ok) {
        const prefs = await response.json();
        setUserPreferences(prefs);
      } else {
        // Use default thresholds if preferences not found
        setUserPreferences({
          global: {
            require_human_review_below: 0.7,
            default_confidence_threshold: 0.5,
          },
        });
      }
    } catch (error) {
      console.error('Error fetching preferences:', error);
      // Use defaults on error
      setUserPreferences({
        global: {
          require_human_review_below: 0.7,
          default_confidence_threshold: 0.5,
        },
      });
    }
  }, [API_ENDPOINT, user?.email, getIdTokenClaims]);

  const fetchVerificationQueue = useCallback(async () => {
    try {
      setLoading(true);
      const tokenClaims = await getIdTokenClaims();
      const idToken = tokenClaims?.__raw;

      // Map one upload record to a queue item.
      const mapUpload = (upload: any): VerificationItem => ({
        report_id: upload.report_id,
        filename: upload.filename || upload.report_id,
        uploaded_at: upload.uploaded_at || upload.ingestion_timestamp,
        confidence: upload.confidence || upload.overall_confidence || 0,
        status: upload.status,
        uploaded_by: upload.uploaded_by,
        page_number: upload.page_number,
        total_pages: upload.total_pages,
        product_type: upload.product_type,
        meter_id: upload.meter_id,
        verified: upload.user_validated || upload.verified || false,
        verification_date: upload.verification_date,
        verified_by: upload.verified_by,
        missing_required_fields: upload.missing_required_fields || [],
        fields_below_threshold: upload.fields_below_threshold || [],
        document_type: upload.document_type || 'proving_report',
        vessel: upload.vessel,
        discharge_date: upload.discharge_date,
        has_bounding_boxes: upload.has_bounding_boxes || false,
      });

      // Fetch pages one at a time and render PROGRESSIVELY: show the first batch
      // immediately, then append each subsequent page. With 600+ docs this drops
      // the time-to-first-rows from "wait for everything" to a single page.
      // Dedup by the TRUE composite identity (report_id + ingestion_timestamp) —
      // report_id (test number) can collide across documents, so it isn't unique.
      const seenKeys = new Set<string>();
      let lastKey: string | null = null;
      let hasMore = true;
      let firstPage = true;
      let total = 0;

      while (hasMore) {
        const url: string = lastKey
          ? `${API_ENDPOINT}/user-uploads?email=${encodeURIComponent(user?.email || '')}&scope=all&limit=200&lastKey=${encodeURIComponent(lastKey)}`
          : `${API_ENDPOINT}/user-uploads?email=${encodeURIComponent(user?.email || '')}&scope=all&limit=200`;

        const response = await fetch(url, { headers: { Authorization: `Bearer ${idToken}` } });
        if (!response.ok) throw new Error('Failed to fetch documents');

        const data = await response.json();
        const uploads: any[] = data.uploads || [];
        const fresh = uploads.filter((u: any) => {
          const k = `${u.report_id}::${u.uploaded_at || u.ingestion_timestamp || ''}`;
          if (seenKeys.has(k)) return false;
          seenKeys.add(k);
          return true;
        }).map(mapUpload);
        total += fresh.length;

        if (firstPage) {
          setItems(fresh);
          setLoading(false);       // first rows on screen right away
          setLoadingMore(true);    // keep a subtle "loading more" indicator
          firstPage = false;
        } else {
          setItems(prev => [...prev, ...fresh]);
        }

        lastKey = data.lastKey;
        hasMore = data.hasMore || false;
      }

      console.log(`Loaded ${total} verification items (progressive)`);
      // applyFilters will be triggered by useEffect when items change
    } catch (error) {
      console.error('Error fetching verification queue:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [API_ENDPOINT, user?.email, getIdTokenClaims]);

  const applyFilters = useCallback((
    itemList: VerificationItem[],
    mode: string,
    search: string,
    filters: typeof activeFilters,
    docType: 'all' | 'proving_report' | 'crude_ticket' = 'all'
  ) => {
    console.log('Applying filters - mode:', mode, 'items:', itemList.length, 'search:', search, 'docType:', docType);
    let filtered = [...itemList];

    // Document-type filter (All / Proving Reports / Tickets)
    if (docType !== 'all') {
      filtered = filtered.filter(item => (item.document_type || 'proving_report') === docType);
    }

    // Get thresholds from user preferences or use defaults
    const reviewThreshold = userPreferences?.global?.require_human_review_below || 0.7;
    const lowConfidenceThreshold = userPreferences?.global?.default_confidence_threshold || 0.5;
    console.log('Thresholds - review:', reviewThreshold, 'low confidence:', lowConfidenceThreshold);

    // Apply mode filter
    switch (mode) {
      case 'needs-review':
        filtered = filtered.filter(
          item => !item.verified &&
          (item.status === 'review' ||
           item.requires_human_review === true ||
           (item.missing_required_fields && item.missing_required_fields.length > 0) ||
           (item.confidence < reviewThreshold && item.status === 'completed'))
        );
        console.log('Needs review filter applied - filtered count:', filtered.length);
        break;
      case 'low-confidence':
        filtered = filtered.filter(item => item.confidence < lowConfidenceThreshold && item.status === 'completed');
        break;
      case 'verified':
        filtered = filtered.filter(item => item.verified);
        break;
      case 'non-verified':
        // Show all documents that haven't been verified (not 100% confidence)
        filtered = filtered.filter(item => !item.verified && item.confidence < 1.0 && (item.status === 'completed' || item.status === 'validated'));
        break;
      case 'all':
      default:
        // Show all completed and validated documents
        filtered = filtered.filter(item => item.status === 'completed' || item.status === 'validated');
        break;
    }

    // Apply active filters
    if (filters.product && filters.product.length > 0) {
      filtered = filtered.filter(item =>
        filters.product!.includes(item.product_type || '')
      );
    }

    if (filters.page && filters.page.length > 0) {
      filtered = filtered.filter(item =>
        filters.page!.includes(item.page_number || 0)
      );
    }

    if (filters.meterId && filters.meterId.length > 0) {
      filtered = filtered.filter(item =>
        filters.meterId!.includes(item.meter_id || '')
      );
    }

    if (filters.date && filters.date.length > 0) {
      filtered = filtered.filter(item => {
        const itemDateStr = new Date(item.uploaded_at).toLocaleDateString();
        return filters.date!.includes(itemDateStr);
      });
    }

    // Apply search filter
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(
        item =>
          item.filename.toLowerCase().includes(searchLower) ||
          item.report_id.toLowerCase().includes(searchLower) ||
          item.meter_id?.toLowerCase().includes(searchLower) ||
          item.vessel?.toLowerCase().includes(searchLower) ||
          item.product_type?.toLowerCase().includes(searchLower)
      );
    }

    setFilteredItems(filtered);
  }, [userPreferences]);

  // Sort function
  const handleSort = (property: keyof VerificationItem) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Apply sorting to filtered items
  const sortItems = useCallback((items: VerificationItem[]) => {
    return [...items].sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';

      // Handle different data types
      if (orderBy === 'uploaded_at') {
        aValue = new Date(aValue as string).getTime();
        bValue = new Date(bValue as string).getTime();
      } else if (orderBy === 'confidence' || orderBy === 'page_number') {
        aValue = Number(aValue);
        bValue = Number(bValue);
      } else {
        // Convert to strings for comparison
        aValue = String(aValue).toLowerCase();
        bValue = String(bValue).toLowerCase();
      }

      if (order === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  }, [orderBy, order]);

  // Fetch preferences and verification queue independently
  useEffect(() => {
    fetchUserPreferences();
    fetchVerificationQueue();
  }, [fetchUserPreferences, fetchVerificationQueue]);

  useEffect(() => {
    // Apply filters when any filter criteria change
    applyFilters(items, filterMode, searchTerm, activeFilters, docTypeFilter);
  }, [items, filterMode, searchTerm, activeFilters, docTypeFilter, applyFilters]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchVerificationQueue();
    setRefreshing(false);
  };

  // Get unique values for each column
  const getUniqueValues = useCallback((field: string) => {
    const values = new Set();
    items.forEach(item => {
      let value;
      switch(field) {
        case 'product':
          value = item.product_type;
          break;
        case 'page':
          value = item.page_number;
          break;
        case 'meterId':
          value = item.meter_id;
          break;
        case 'date':
          value = new Date(item.uploaded_at).toLocaleDateString();
          break;
        default:
          return;
      }
      if (value && value !== '-') {
        values.add(value);
      }
    });
    return Array.from(values).sort();
  }, [items]);

  // Handle filter menu open
  const handleFilterClick = (event: React.MouseEvent<HTMLElement>, field: string) => {
    event.stopPropagation();
    event.preventDefault();
    // Store the element reference before any async operations
    const element = event.currentTarget;
    const options = getUniqueValues(field);
    setFilterOptions(prev => ({ ...prev, [field]: options }));
    setAnchorEl(prev => ({ ...prev, [field]: element }));
  };

  // Handle filter menu close
  const handleFilterClose = (field: string) => {
    setAnchorEl(prev => ({ ...prev, [field]: null }));
  };

  // Handle filter selection
  const handleFilterSelect = (field: keyof typeof activeFilters, value: any) => {
    setActiveFilters(prev => {
      const next = { ...prev };
      const currentValues = (next[field] || []) as any[];

      if (currentValues.includes(value)) {
        // Remove value
        next[field] = currentValues.filter(v => v !== value) as any;
        if (next[field]?.length === 0) {
          delete next[field];
        }
      } else {
        // Add value
        next[field] = [...currentValues, value] as any;
      }

      return next;
    });
  };

  // Clear filters for a field
  const clearFieldFilters = (field: keyof typeof activeFilters) => {
    setActiveFilters(prev => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
    handleFilterClose(field);
  };

  const handleViewReport = async (reportId: string, pageNumber?: number, documentType?: string) => {
    try {
      const tokenClaims = await getIdTokenClaims();
      const idToken = tokenClaims?.__raw;

      // Route the read to the right table: tickets carry a ?type=ticket hint.
      const typeQuery = documentType === 'crude_ticket' ? '?type=ticket' : '';
      const response = await fetch(
        `${API_ENDPOINT}/report/${encodeURIComponent(reportId)}${typeQuery}`,
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        }
      );

      if (!response.ok) throw new Error('Failed to fetch report');

      const data = await response.json();
      // Add page number to the report data if it's a multi-page PDF
      if (pageNumber) {
        data.page_number = pageNumber;
      }
      // Carry the document type through so the viewer can render the right schema.
      if (documentType) {
        data.document_type = documentType;
      }
      setSelectedReport(data);
      setShowDataViewer(true);
    } catch (error) {
      console.error('Error fetching report:', error);
    }
  };

  // After saving/verifying, jump straight to the next unverified report in the
  // current (filtered) list so bulk verification is a fast assembly line.
  const goToNextUnverified = (currentReportId?: string) => {
    const next = filteredItems.find(
      (i) => i.report_id !== currentReportId && !i.verified
    );
    if (next) {
      handleViewReport(next.report_id, (next as any).page_number, next.document_type);
    } else {
      // Nothing left to verify — return to the queue.
      setShowDataViewer(false);
      setSelectedReport(null);
    }
  };

  const getConfidenceColor = (confidence: number): 'error' | 'warning' | 'success' => {
    const lowConfidenceThreshold = userPreferences?.global?.default_confidence_threshold || 0.5;
    const reviewThreshold = userPreferences?.global?.require_human_review_below || 0.7;

    if (confidence < lowConfidenceThreshold) return 'error';
    if (confidence < reviewThreshold) return 'warning';
    return 'success';
  };

  const getConfidenceIcon = (confidence: number, verified: boolean) => {
    const lowConfidenceThreshold = userPreferences?.global?.default_confidence_threshold || 0.5;
    const reviewThreshold = userPreferences?.global?.require_human_review_below || 0.7;

    if (verified) {
      return <CheckCircleIcon color="success" />;
    }
    if (confidence < lowConfidenceThreshold) {
      return <ErrorIcon color="error" />;
    }
    if (confidence < reviewThreshold) {
      return <WarningIcon color="warning" />;
    }
    return <CheckCircleIcon color="success" />;
  };

  const formatPageInfo = (pageNumber?: number, totalPages?: number) => {
    // Always show page number if provided
    if (pageNumber) {
      if (totalPages && totalPages > 1) {
        // Multi-page document
        return (
          <Chip
            size="small"
            label={`Page ${pageNumber}/${totalPages}`}
            icon={<DescriptionIcon />}
            variant="outlined"
            color="primary"
          />
        );
      } else {
        // Single page or page number provided without total
        return (
          <Typography variant="body2" color="text.secondary">
            Page {pageNumber}
          </Typography>
        );
      }
    }

    // No page number available
    return '-';
  };

  if (showDataViewer && selectedReport) {
    return (
      <Box>
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setShowDataViewer(false);
              setSelectedReport(null);
            }}
          >
            ← Back to Queue
          </Button>
        </Box>
        <DataViewerSideBySide
          reportData={selectedReport}
          onClose={() => {
            setShowDataViewer(false);
            setSelectedReport(null);
          }}
          onSaveAndNext={() => goToNextUnverified(selectedReport?.report_id)}
          remainingCount={filteredItems.filter(
            (i) => !i.verified && i.report_id !== selectedReport?.report_id
          ).length}
          onSave={(updatedReport) => {
            // Update the selectedReport to reflect the saved changes
            setSelectedReport(updatedReport);

            // Update the specific item in the list without fetching everything
            setItems(prevItems => prevItems.map(item =>
              item.report_id === updatedReport.report_id
                ? {
                    ...item,
                    meter_id: updatedReport.extracted_data?.meter_id?.value || item.meter_id,
                    product_type: updatedReport.extracted_data?.product_type?.value || item.product_type,
                    confidence: updatedReport.overall_confidence || item.confidence,
                    status: updatedReport.user_validated ? 'validated' : item.status,
                    verified: updatedReport.user_validated || false,
                    verification_date: updatedReport.user_validated ? new Date().toISOString() : item.verification_date
                  }
                : item
            ));
          }}
        />
      </Box>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5" component="h2" sx={{ display: 'flex', alignItems: 'center' }}>
              Verification Queue
              {filterMode === 'needs-review' && (
                <Badge
                  badgeContent={filteredItems.length}
                  color="error"
                  sx={{ ml: 2 }}
                  max={99}
                />
              )}
              {loadingMore && (
                <Chip
                  size="small"
                  icon={<CircularProgress size={12} />}
                  label={`Loading more… (${items.length})`}
                  variant="outlined"
                  sx={{ ml: 2 }}
                />
              )}
            </Typography>
            <Button
              variant="outlined"
              startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={handleRefresh}
              disabled={refreshing}
            >
              Refresh
            </Button>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 250 }}
            />

            {/* Active Filters Display */}
            {Object.keys(activeFilters).length > 0 && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                {activeFilters.product && activeFilters.product.length > 0 && (
                  <Chip
                    label={`Product: ${activeFilters.product.join(', ')}`}
                    onDelete={() => setActiveFilters(prev => { const next = {...prev}; delete next.product; return next; })}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
                {activeFilters.page && activeFilters.page.length > 0 && (
                  <Chip
                    label={`Page: ${activeFilters.page.join(', ')}`}
                    onDelete={() => setActiveFilters(prev => { const next = {...prev}; delete next.page; return next; })}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
                {activeFilters.meterId && activeFilters.meterId.length > 0 && (
                  <Chip
                    label={`Meter: ${activeFilters.meterId.join(', ')}`}
                    onDelete={() => setActiveFilters(prev => { const next = {...prev}; delete next.meterId; return next; })}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
                {activeFilters.date && activeFilters.date.length > 0 && (
                  <Chip
                    label={`Date: ${activeFilters.date.join(', ')}`}
                    onDelete={() => setActiveFilters(prev => { const next = {...prev}; delete next.date; return next; })}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
                <Button
                  size="small"
                  onClick={() => setActiveFilters({})}
                  startIcon={<FilterIcon />}
                >
                  Clear All
                </Button>
              </Box>
            )}

            <ToggleButtonGroup
              value={filterMode}
              exclusive
              onChange={(_, value) => value && setFilterMode(value)}
              size="small"
            >
              <ToggleButton value="needs-review" color="error">
                <Badge
                  badgeContent={items.filter(i =>
                    i.confidence < (userPreferences?.global?.require_human_review_below || 0.7) &&
                    !i.verified &&
                    i.status === 'completed'
                  ).length}
                  color="error"
                >
                  <FilterIcon sx={{ mr: 1 }} />
                </Badge>
                Needs Review {userPreferences?.global?.require_human_review_below &&
                  `(<${(userPreferences.global?.require_human_review_below * 100).toFixed(0)}%)`}
              </ToggleButton>
              <ToggleButton value="low-confidence" color="warning">
                Low Confidence {userPreferences?.global?.default_confidence_threshold &&
                  `(<${(userPreferences.global?.default_confidence_threshold * 100).toFixed(0)}%)`}
              </ToggleButton>
              <ToggleButton value="verified" color="success">
                Verified
              </ToggleButton>
              <ToggleButton value="non-verified" color="primary">
                <Badge
                  badgeContent={items.filter(i => !i.verified && i.confidence < 1.0).length}
                  color="primary"
                >
                  Non-Verified
                </Badge>
              </ToggleButton>
              <ToggleButton value="all">
                All Documents
              </ToggleButton>
            </ToggleButtonGroup>

            {/* Document-type filter: All / Proving Reports / Tickets */}
            <ToggleButtonGroup
              value={docTypeFilter}
              exclusive
              onChange={(_, value) => value && setDocTypeFilter(value)}
              size="small"
            >
              <ToggleButton value="all">
                All Types
              </ToggleButton>
              <ToggleButton value="proving_report" color="primary">
                <DescriptionIcon fontSize="small" sx={{ mr: 0.5 }} />
                Proving Reports
              </ToggleButton>
              <ToggleButton value="crude_ticket" color="secondary">
                <Badge
                  badgeContent={items.filter(i => i.document_type === 'crude_ticket').length}
                  color="secondary"
                >
                  <LocalShippingIcon fontSize="small" sx={{ mr: 0.5 }} />
                </Badge>
                Tickets
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {userPreferences?.global && (
            <Alert severity="info" icon={false} sx={{ mb: 1 }}>
              <Typography variant="body2">
                Using your personalized thresholds: Documents with confidence below{' '}
                <strong>{((userPreferences.global?.require_human_review_below || 0.7) * 100).toFixed(0)}%</strong> need review,
                and below <strong>{((userPreferences.global?.default_confidence_threshold || 0.5) * 100).toFixed(0)}%</strong> are
                considered low confidence.{' '}
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => window.location.hash = '#settings'}
                  sx={{ textDecoration: 'underline' }}
                >
                  Adjust in Settings
                </Link>
              </Typography>
            </Alert>
          )}

          {filterMode === 'needs-review' && filteredItems.length === 0 && (
            <Alert severity="success">
              No documents need review! All documents have confidence above{' '}
              {((userPreferences?.global?.require_human_review_below || 0.7) * 100).toFixed(0)}% or have been verified.
            </Alert>
          )}
        </Stack>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? order : 'asc'}
                  onClick={() => handleSort('status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'filename'}
                  direction={orderBy === 'filename' ? order : 'asc'}
                  onClick={() => handleSort('filename')}
                >
                  Document
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'document_type'}
                  direction={orderBy === 'document_type' ? order : 'asc'}
                  onClick={() => handleSort('document_type')}
                >
                  Type
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <TableSortLabel
                    active={orderBy === 'page_number'}
                    direction={orderBy === 'page_number' ? order : 'asc'}
                    onClick={() => handleSort('page_number')}
                  >
                    Page
                  </TableSortLabel>
                  <IconButton
                    size="small"
                    onClick={(e) => handleFilterClick(e, 'page')}
                    sx={{ padding: 0.5 }}
                  >
                    <Badge
                      color="primary"
                      variant="dot"
                      invisible={!activeFilters.page || activeFilters.page.length === 0}
                    >
                      <FilterIcon fontSize="small" />
                    </Badge>
                  </IconButton>
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <TableSortLabel
                    active={orderBy === 'meter_id'}
                    direction={orderBy === 'meter_id' ? order : 'asc'}
                    onClick={() => handleSort('meter_id')}
                  >
                    Meter / Vessel
                  </TableSortLabel>
                  <IconButton
                    size="small"
                    onClick={(e) => handleFilterClick(e, 'meterId')}
                    sx={{ padding: 0.5 }}
                  >
                    <Badge
                      color="primary"
                      variant="dot"
                      invisible={!activeFilters.meterId || activeFilters.meterId.length === 0}
                    >
                      <FilterIcon fontSize="small" />
                    </Badge>
                  </IconButton>
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <TableSortLabel
                    active={orderBy === 'product_type'}
                    direction={orderBy === 'product_type' ? order : 'asc'}
                    onClick={() => handleSort('product_type')}
                  >
                    Product
                  </TableSortLabel>
                  <IconButton
                    size="small"
                    onClick={(e) => handleFilterClick(e, 'product')}
                    sx={{ padding: 0.5 }}
                  >
                    <Badge
                      color="primary"
                      variant="dot"
                      invisible={!activeFilters.product || activeFilters.product.length === 0}
                    >
                      <FilterIcon fontSize="small" />
                    </Badge>
                  </IconButton>
                </Box>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'confidence'}
                  direction={orderBy === 'confidence' ? order : 'asc'}
                  onClick={() => handleSort('confidence')}
                >
                  Confidence
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <TableSortLabel
                    active={orderBy === 'uploaded_at'}
                    direction={orderBy === 'uploaded_at' ? order : 'asc'}
                    onClick={() => handleSort('uploaded_at')}
                  >
                    Uploaded
                  </TableSortLabel>
                  <IconButton
                    size="small"
                    onClick={(e) => handleFilterClick(e, 'date')}
                    sx={{ padding: 0.5 }}
                  >
                    <Badge
                      color="primary"
                      variant="dot"
                      invisible={!activeFilters.date || activeFilters.date.length === 0}
                    >
                      <FilterIcon fontSize="small" />
                    </Badge>
                  </IconButton>
                </Box>
              </TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography color="text.secondary">
                    No documents found matching your criteria
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sortItems(filteredItems)
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((item) => (
                  <TableRow
                    key={`${item.report_id}-${item.uploaded_at || item.page_number || 0}`}
                    sx={{
                      backgroundColor: item.verified
                        ? 'success.light'
                        : item.confidence < (userPreferences?.global?.default_confidence_threshold || 0.5)
                        ? 'error.light'
                        : item.confidence < (userPreferences?.global?.require_human_review_below || 0.7)
                        ? 'warning.light'
                        : 'inherit',
                      opacity: item.verified ? 0.8 : 1,
                    }}
                  >
                    <TableCell>
                      <Tooltip
                        title={
                          item.verified
                            ? `Verified on ${new Date(item.verification_date!).toLocaleDateString()}`
                            : (item.missing_required_fields && item.missing_required_fields.length > 0)
                            ? `Missing required fields: ${item.missing_required_fields.join(', ')}`
                            : item.confidence < (userPreferences?.global?.require_human_review_below || 0.7)
                            ? `Needs review (confidence ${(item.confidence * 100).toFixed(0)}% is below your ${((userPreferences?.global?.require_human_review_below || 0.7) * 100).toFixed(0)}% threshold)`
                            : 'High confidence'
                        }
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {getConfidenceIcon(item.confidence, item.verified || false)}
                          {item.missing_required_fields && item.missing_required_fields.length > 0 && (
                            <Chip
                              label={`${item.missing_required_fields.length} missing`}
                              size="small"
                              color="error"
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={item.filename}>
                        <Typography variant="body2">
                          {item.filename}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {item.document_type === 'crude_ticket' ? (
                        <Chip
                          icon={<LocalShippingIcon />}
                          label="Ticket"
                          size="small"
                          color="secondary"
                          variant="outlined"
                          sx={{ height: 22, fontSize: '0.7rem' }}
                        />
                      ) : (
                        <Chip
                          icon={<DescriptionIcon />}
                          label="Proving"
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ height: 22, fontSize: '0.7rem' }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {item.document_type === 'crude_ticket'
                        ? formatPageInfo(undefined, undefined)
                        : formatPageInfo(item.page_number, item.total_pages)}
                    </TableCell>
                    <TableCell>
                      {item.document_type === 'crude_ticket'
                        ? (item.vessel || '-')
                        : (item.meter_id || '-')}
                    </TableCell>
                    <TableCell>{item.product_type || '-'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Chip
                          label={`${(item.confidence * 100).toFixed(0)}%`}
                          size="small"
                          color={getConfidenceColor(item.confidence)}
                          variant={item.verified ? 'filled' : 'outlined'}
                        />
                        {item.fields_below_threshold && item.fields_below_threshold.length > 0 && (
                          <Tooltip title={`Low confidence fields: ${item.fields_below_threshold.join(', ')}`}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                              {item.fields_below_threshold.length} low fields
                            </Typography>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {new Date(item.uploaded_at).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Review & Edit">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleViewReport(item.report_id, item.page_number, item.document_type)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="View Document">
                        <IconButton
                          size="small"
                          onClick={() => handleViewReport(item.report_id, item.page_number, item.document_type)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredItems.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>

      {/* Filter Dropdown Menus */}
      {['product', 'page', 'meterId', 'date'].map((field) => (
        <Menu
          key={field}
          anchorEl={anchorEl[field]}
          open={Boolean(anchorEl[field])}
          onClose={() => handleFilterClose(field)}
          PaperProps={{
            style: {
              maxHeight: 300,
              width: '250px',
            },
          }}
        >
          <MenuItem dense>
            <Button
              size="small"
              fullWidth
              onClick={() => clearFieldFilters(field as keyof typeof activeFilters)}
            >
              Clear Filter
            </Button>
          </MenuItem>
          <Divider />
          {filterOptions[field]?.map((option) => (
            <MenuItem
              key={option}
              dense
              onClick={() => handleFilterSelect(field as keyof typeof activeFilters, option)}
            >
              <Checkbox
                checked={(activeFilters[field as keyof typeof activeFilters] as any[])?.includes(option) || false}
                size="small"
              />
              <ListItemText primary={option} />
            </MenuItem>
          ))}
        </Menu>
      ))}
    </Box>
  );
};

export default VerificationQueue;